<script setup>
import { ref } from 'vue';

import { storeToRefs } from 'pinia';

import { ChevronDown, ChevronUp, RefreshCw } from 'lucide-vue-next';

import { useAppVersionStore } from '@/stores/appVersion';

import VersionUpdateNotes from './VersionUpdateNotes.vue';

defineEmits(['reload', 'dismiss']);

const { notes, notesMode, prs } = storeToRefs(useAppVersionStore());

const expanded = ref(false);
</script>

<template>
  <div
    class="version-update-notice"
    role="status"
  >
    <div class="version-update-notice-header">
      <RefreshCw
        :size="18"
        class="version-update-notice-icon"
      />
      <div class="version-update-notice-text">
        <p class="version-update-notice-title">
          {{ $t('A new version is available') }}
        </p>
        <p class="version-update-notice-hint">
          {{ $t('You can keep working and reload when it suits you.') }}
        </p>
      </div>
    </div>

    <template v-if="notes || prs.length">
      <button
        type="button"
        class="version-update-notice-toggle"
        :aria-expanded="expanded"
        @click="expanded = !expanded"
      >
        <component
          :is="expanded ? ChevronUp : ChevronDown"
          :size="14"
        />
        {{ expanded ? $t('Hide details') : $t('Show details') }}
      </button>
      <VersionUpdateNotes
        v-if="expanded"
        :notes="notes"
        :notes-mode="notesMode"
        :prs="prs"
      />
    </template>

    <div class="version-update-notice-actions">
      <el-button
        size="small"
        @click="$emit('dismiss')"
      >
        {{ $t('Later') }}
      </el-button>
      <el-button
        size="small"
        type="primary"
        @click="$emit('reload')"
      >
        {{ $t('Reload') }}
      </el-button>
    </div>
  </div>
</template>

<style scoped>
.version-update-notice {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 340px;
  max-width: calc(100vw - 40px);
  padding: 14px 16px;
  border: 1px solid var(--wp-border-primary);
  border-radius: 12px;
  background: var(--wp-surface-card);
  box-shadow: 0 8px 24px var(--wp-shadow-card);
}

.version-update-notice-header {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.version-update-notice-icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--wp-text-accent);
}

.version-update-notice-title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--wp-text-primary);
}

.version-update-notice-hint {
  margin: 2px 0 0 0;
  font-size: 0.8125rem;
  color: var(--wp-text-tertiary);
}

.version-update-notice-toggle {
  display: inline-flex;
  gap: 4px;
  align-items: center;
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  font-size: 0.8125rem;
  color: var(--wp-text-link);
}

.version-update-notice-toggle:hover {
  color: var(--wp-text-link-hover);
}

.version-update-notice-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
</style>
